import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { Text } from 'components';
import { ObjectId } from '@bb/domain/dist/mongodb';
import React from 'react';
import { featureEnabled, formatCurrency, getProvinceAbbreviation } from 'utils';
import { ListingLabelContext } from 'contexts';
import { Paragraph } from '../../Typography';
import {
  DotIcon,
  NoWrapRow,
  NoWrapText,
  StyledAvailability,
  WrapRow,
} from '../StyledComponents';

type ListingInfoProps = {
  line1?: string;
  line2?: string;
  province?: string;
  city?: string;
  availability?: string;
  price?: number;
  status?: {
    type: string;
    mlsNumbers: BuildJsonType<{
      board: string;
      boardId: ObjectId;
      number: string;
    }>[];
  };
  dom?: number;
  mlsTypes?: string[];
};

const ListingInfo: React.FC<ListingInfoProps> = ({
  line1,
  line2,
  province,
  city,
  availability,
  price,
  status,
  dom,
  mlsTypes,
}) => {
  const { getLabel, getAvailability, getBoardName } =
    React.useContext(ListingLabelContext);

  return (
    <>
      <NoWrapRow>
        <StyledAvailability
          availability={availability}
          data-cy="listing-availability"
          $hasSupraRebrandingFeature={featureEnabled(
            'supraRebrandingFeature',
            1,
          )}
        >
          {getAvailability(availability)}
        </StyledAvailability>
        <DotIcon />
        <NoWrapText
          type="secondary"
          ellipsis={status?.mlsNumbers?.length > 1 ? 190 : false}
          data-cy="board-numbers-or-status"
        >
          {status.type === 'MLS' && status?.mlsNumbers?.length
            ? status?.mlsNumbers?.map(({ board, number }, i) => (
                <span key={`${number}${i}`}>
                  {getBoardName(board, false)}: {number}
                  {i < status.mlsNumbers.length - 1 && ', '}
                </span>
              ))
            : getLabel(status.type)}
        </NoWrapText>
        {mlsTypes && (
          <>
            <DotIcon />
            <NoWrapText type="secondary" data-cy="listing-mls-types">
              {mlsTypes.reduce((prev: string, curr: string) => `${prev ? `${prev},` : ''}  ${curr}`, '')}
            </NoWrapText>
          </>
        )}
      </NoWrapRow>
      <WrapRow>
        <Paragraph
          strong
          data-cy="address-line1"
          ellipsis={{
            rows: 2,
            suffix: line2 && `, ${line2}`,
            tooltip: true,
          }}
          style={{ margin: 0, marginRight: '5px' }}
        >
          {line1.trim()}
        </Paragraph>
        <span>
          <NoWrapText type="secondary" data-cy="address-city">
            {city}
          </NoWrapText>
          <NoWrapText type="secondary" data-cy="address-province">
            ,&nbsp;{getProvinceAbbreviation(province)}
          </NoWrapText>
        </span>
      </WrapRow>
      <NoWrapRow>
        <Text data-cy="listing-price" style={{ marginRight: '5px' }}>
          {formatCurrency(price)}
        </Text>
        <Tooltip
          title={
            typeof dom === 'number'
              ? `${dom} ${dom === 1 ? 'Day' : 'Days'} on Market`
              : 'No Data Available'
          }
        >
          <Text type="secondary" data-cy="listing-time">
            <NoWrapRow style={{ marginLeft: '6px' }}>
              <Icon
                type="antdClockCircle"
                width={9}
                height={9}
                style={{ marginRight: '4px' }}
              />
              {typeof dom === 'number' ? dom : '-'} d
            </NoWrapRow>
          </Text>
        </Tooltip>
      </NoWrapRow>
    </>
  );
};

export default ListingInfo;
